import {
  Button,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Switch,
  Text,
  VStack,
} from '@chakra-ui/react';
import { type MultiValue, Select } from 'chakra-react-select';
import { useState } from 'react';
import { useQuery } from 'urql';

import { AllClientsQuery } from '../userOperations';
import { useUsageAnalyticsExport } from './useUsageAnalyticsExport';

const iso = (d: Date): string => d.toISOString().slice(0, 10);
const daysAgo = (n: number): Date => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d;
};

type Option = { label: string; value: string };

interface UsageAnalyticsMultiModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// Cross-group export: pick any number of client groups and download a single
// workbook (Summary breaks the metrics out per group). The per-group button on
// the Edit-client-group page covers the single-group case.
export function UsageAnalyticsMultiModal({
  isOpen,
  onClose,
}: UsageAnalyticsMultiModalProps) {
  const [{ data, fetching }] = useQuery({ query: AllClientsQuery });
  const options: Option[] = (data?.clients ?? [])
    .flatMap((c) => (c?.client_name ? [c.client_name] : []))
    .sort((a, b) => a.localeCompare(b))
    .map((n) => ({ label: n, value: n }));

  const [selected, setSelected] = useState<MultiValue<Option>>([]);
  const [from, setFrom] = useState(iso(daysAgo(30)));
  const [to, setTo] = useState(iso(new Date()));
  const [includeSessionDetail, setIncludeSessionDetail] = useState(false);
  const { exportUsage, isLoading, error } = useUsageAnalyticsExport();

  const clientNames = selected.map((o) => o.value);

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          Download usage analytics — multiple client groups
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align="stretch" spacing={4}>
            <FormControl>
              <FormLabel>Client groups</FormLabel>
              <Select
                isMulti
                isLoading={fetching}
                options={options}
                value={selected}
                onChange={setSelected}
                placeholder="Select one or more client groups…"
                closeMenuOnSelect={false}
              />
            </FormControl>
            <HStack>
              <FormControl>
                <FormLabel>From</FormLabel>
                <Input
                  type="date"
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                />
              </FormControl>
              <FormControl>
                <FormLabel>To</FormLabel>
                <Input
                  type="date"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                />
              </FormControl>
            </HStack>
            <FormControl display="flex" alignItems="center">
              <FormLabel mb={0}>Include per-session detail</FormLabel>
              <Switch
                isChecked={includeSessionDetail}
                onChange={(e) => setIncludeSessionDetail(e.target.checked)}
              />
            </FormControl>
            {error && (
              <Text color="red.500" fontSize="sm">
                {error}
              </Text>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorScheme="green"
            isLoading={isLoading}
            isDisabled={clientNames.length === 0}
            onClick={() =>
              exportUsage({ clientNames, from, to, includeSessionDetail })
            }
          >
            Generate
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
